import { List } from 'immutable';
import { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { canWrite, getScopes, isJitaUser } from 'documents-ui/lib/permissions';
import * as DocumentsActions from 'documents-ui/actions/DocumentsActions';
import * as FolderActions from 'documents-ui/actions/FolderActions';
import * as UploadActions from 'documents-ui/actions/UploadActions';
import { getPortalIsAtLimit } from 'documents-ui/selectors/UsageSelectors';
import AssignmentPanel from 'sales-content-partitioning/panels/AssignmentPanel';
import IndexHeaderButtons from 'SalesContentIndexUI/components/header/IndexHeaderButtons';
import IndexContainer from 'SalesContentIndexUI/containers/IndexContainer';
import SearchContentTypes from 'SalesContentIndexUI/data/constants/SearchContentTypes';
import IndexBodySlot from 'SalesContentIndexUI/slots/IndexBodySlot';
import DocumentsIndexTable from 'documents-ui/components/DocumentsIndexTable';
import DocumentsCreateButton from 'documents-ui/components/header/DocumentsCreateButton';
import { Loadable } from 'foundations-components/transitional/utils/Loadable';
import { VIEWED_INDEX } from 'documents-lib/js/tracking/actions';
import { trackInteraction, trackPermissionSave } from 'documents-lib/js/tracking/tracker';
import { useFeedbackSurvey } from 'documents-ui/components/hooks/useFeedbackSurvey';
import { useIsFTS0015Variant } from '../hooks/fts0015Hooks';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LoadableDocumentsUsageLimitBanner = Loadable({
  loader: () => import( /* webpackChunkName: "upgrade-components" */'../components/upgradeComponents').then(({
    DocumentsUsageLimitBanner
  }) => DocumentsUsageLimitBanner)
});
const LIMIT_THRESHOLD = 0.5;
const DocumentsIndexContainer = ({
  usage,
  fetchDocumentsUsage,
  saveFolder,
  deleteFolders,
  removeDocuments,
  moveDocuments,
  uploadFile,
  importFile,
  portalIsAtLimit,
  history,
  location
}) => {
  useEffect(() => {
    if (usage) {
      return;
    }
    trackInteraction(VIEWED_INDEX);
    fetchDocumentsUsage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Render CSAT survey
  useFeedbackSurvey();
  const hasFreeLimit = (usage === null || usage === void 0 ? void 0 : usage.limit) === 5;
  const showUsageLimit = useMemo(() => hasFreeLimit && (usage === null || usage === void 0 ? void 0 : usage.count) > 0 && !isJitaUser(), [usage, hasFreeLimit]);
  const showUsageLimitWithThreshold = useMemo(() => hasFreeLimit && (usage === null || usage === void 0 ? void 0 : usage.count) > 0 && !isJitaUser() && (usage === null || usage === void 0 ? void 0 : usage.count) / (usage === null || usage === void 0 ? void 0 : usage.limit) > LIMIT_THRESHOLD, [usage, hasFreeLimit]);
  const {
    isFTS0015Variant,
    isFTS0015Fetched
  } = useIsFTS0015Variant(showUsageLimitWithThreshold);
  const renderUsageLimitBanner = () => {
    if (!showUsageLimit) {
      return null;
    }
    return /*#__PURE__*/_jsx(LoadableDocumentsUsageLimitBanner, {
      count: usage.count
    });
  };
  const renderBody = () => {
    return /*#__PURE__*/_jsxs(IndexBodySlot, {
      children: [isFTS0015Fetched && !isFTS0015Variant && renderUsageLimitBanner(), /*#__PURE__*/_jsx(DocumentsIndexTable, {
        saveFolder: saveFolder,
        deleteFolders: deleteFolders
        // @ts-expect-error removeDocuments does not exist in props (but it does)
        ,
        removeDocuments: removeDocuments,
        moveDocuments: moveDocuments
      }), /*#__PURE__*/_jsx(AssignmentPanel, {
        scopes: List(getScopes()),
        afterSave: trackPermissionSave
      })]
    });
  };
  const renderHeaderButtons = () => {
    return /*#__PURE__*/_jsx(IndexHeaderButtons, {
      createButton: /*#__PURE__*/_jsx(DocumentsCreateButton, {
        usage: usage,
        uploadFile: uploadFile,
        importFile: importFile,
        portalIsAtLimit: portalIsAtLimit,
        buttonSize: "small",
        buttonUse: "primary",
        location: location
      }),
      usage: usage,
      usageLimitCounterUpgradeKey: isFTS0015Fetched && isFTS0015Variant ? 'fts-0003-documents-usage-limit-index' : undefined,
      contentType: SearchContentTypes.DOCUMENT,
      saveFolder: saveFolder,
      location: location,
      showNewFolderButton: canWrite(),
      extraButtons: []
    });
  };
  return /*#__PURE__*/_jsxs(IndexContainer, {
    history: history,
    location: location,
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "app.title"
    }),
    children: [renderHeaderButtons(), renderBody()]
  });
};
export default connect(state => {
  return {
    gates: state.auth.gates,
    usage: state.documents.usage,
    portalIsAtLimit: getPortalIsAtLimit(state)
  };
}, Object.assign({}, FolderActions, DocumentsActions, UploadActions))(withRouter(DocumentsIndexContainer));